import { useState, useRef, useEffect } from 'react';

// Microphone access for the Voice panel: permission, the list of input
// devices, the live level meter, and the "Test" button.
//
// `isListeningRef` is owned by the caller (the speech hook flips it); the
// meter loop stops itself when it goes false.
export default function useMicrophone({ addDebugLog, isListeningRef, audioGain }) {
  const [micPermission, setMicPermission] = useState('checking');
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('default');
  const [audioLevel, setAudioLevel] = useState(0);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const micStreamRef = useRef(null);
  const animationFrameRef = useRef(null);

  const constraintsFor = (deviceId) => ({
    audio: deviceId === 'default' ? true : { deviceId: { exact: deviceId } }
  });

  const enumerateAudioDevices = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      setAudioDevices(audioInputs);
      addDebugLog(`🎙️ Found ${audioInputs.length} audio input device(s)`);
      return audioInputs;
    } catch (error) {
      console.error('Error enumerating devices:', error);
      addDebugLog(`❌ Failed to enumerate devices: ${error.message}`);
      return [];
    }
  };

  const startAudioMonitoring = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraintsFor(selectedDeviceId));
      micStreamRef.current = stream;

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const microphone = audioContextRef.current.createMediaStreamSource(stream);

      analyserRef.current.fftSize = 256;
      microphone.connect(analyserRef.current);

      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateLevel = () => {
        if (!isListeningRef.current) return;
        analyserRef.current.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / bufferLength;
        const boostedLevel = (average / 128) * (audioGain * 100);
        setAudioLevel(Math.min(100, boostedLevel));
        animationFrameRef.current = requestAnimationFrame(updateLevel);
      };

      updateLevel();
    } catch (err) {
      console.error('Audio monitoring error:', err);
    }
  };

  const stopAudioMonitoring = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setAudioLevel(0);
  };

  const testMicrophone = async () => {
    addDebugLog('🔊 Testing microphone...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraintsFor(selectedDeviceId));
      addDebugLog('✅ Microphone working!');
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      addDebugLog(`❌ Mic test failed: ${err.message}`);
    }
  };

  // Ask for permission once on mount (this also unlocks device names), and
  // release the microphone when the app closes.
  useEffect(() => {
    const checkMicPermission = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        setMicPermission('granted');
        addDebugLog('✅ Microphone permission granted');
        await enumerateAudioDevices();
      } catch (err) {
        setMicPermission('denied');
        addDebugLog('❌ Microphone permission denied');
      }
    };

    checkMicPermission();

    return () => stopAudioMonitoring();
    // Mount-only by design; the helpers only touch refs and stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    micPermission,
    audioDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    audioLevel,
    startAudioMonitoring,
    stopAudioMonitoring,
    testMicrophone,
  };
}
